import { Middleware, AnyAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { analyticsService } from './AnalyticsService';
import { EventParameters } from '../types/analytics';

/**
 * Configuration for analytics middleware
 */
export interface AnalyticsMiddlewareConfig {
  trackedActions: string[];
  eventMappings: Record<string, EventMapping>;
  batchSize: number;
  flushInterval: number;
}

/**
 * Event mapping configuration for Redux actions
 */
export interface EventMapping {
  eventName: string;
  parameterExtractor: (action: AnyAction, state?: RootState) => EventParameters;
}

/**
 * Batched event for processing
 */
interface BatchedEvent {
  eventName: string;
  parameters: EventParameters;
  timestamp: number;
}

/**
 * Redux Analytics Middleware
 * Automatically tracks relevant Redux actions and converts them to analytics events
 */
export class AnalyticsMiddleware {
  private config: AnalyticsMiddlewareConfig;
  private eventBatch: BatchedEvent[] = [];
  private flushTimer: NodeJS.Timeout | null = null;

  /**
   * Constructor for AnalyticsMiddleware
   * @param {Partial<AnalyticsMiddlewareConfig>} config - Configuration options
   */
  constructor(config: Partial<AnalyticsMiddlewareConfig> = {}) {
    this.config = {
      trackedActions: [
        'boatList/add',
        'boatList/remove',
        'boatList/update',
        'boatList/selectBaseBoat',
        'boatList/replaceAll',
        'boatList/addGroup',
        'boatList/removeGroup',
        'boatList/updateGroup',
        'boatList/selectCurrentGroup',
      ],
      eventMappings: this.createDefaultEventMappings(),
      batchSize: 10,
      flushInterval: 5000, // 5 seconds
      ...config,
    };
  }

  /**
   * Create the Redux middleware function
   * @return {Middleware} The Redux middleware
   */
  createMiddleware(): Middleware<Record<string, never>, RootState> {
    return (store) => (next) => (action) => {
      try {
        // Always let the action pass through first
        const result = next(action);

        // Track the action if it's in our tracked list
        this.trackAction(action as AnyAction, store.getState());

        return result;
      } catch (error) {
        // Ensure analytics errors don't break the application
        console.error('AnalyticsMiddleware: Error processing action:', error);
        return next(action);
      }
    };
  }

  /**
   * Track a Redux action by converting it to an analytics event
   * @param {AnyAction} action - The Redux action
   * @param {RootState} state - The current Redux state
   */
  private trackAction(action: AnyAction, state: RootState): void {
    try {
      // Check if this action should be tracked
      if (!this.shouldTrackAction(action)) {
        return;
      }

      // Get event mapping for this action
      const mapping = this.config.eventMappings[action.type];
      if (!mapping) {
        console.warn(`AnalyticsMiddleware: No event mapping found for action ${action.type}`);
        return;
      }

      // Extract parameters using the mapping
      const parameters = mapping.parameterExtractor(action, state);

      // Add to batch
      this.addToBatch(mapping.eventName, parameters);
    } catch (error) {
      console.error('AnalyticsMiddleware: Error tracking action:', error);
    }
  }

  /**
   * Check if an action should be tracked
   * @param {AnyAction} action - The Redux action
   * @return {boolean} True if action should be tracked
   */
  private shouldTrackAction(action: AnyAction): boolean {
    return this.config.trackedActions.includes(action.type);
  }

  /**
   * Add event to batch for processing
   * @param {string} eventName - Name of the event
   * @param {EventParameters} parameters - Event parameters
   */
  private addToBatch(eventName: string, parameters: EventParameters): void {
    const batchedEvent: BatchedEvent = {
      eventName,
      parameters,
      timestamp: Date.now(),
    };

    this.eventBatch.push(batchedEvent);

    // Flush if batch is full
    if (this.eventBatch.length >= this.config.batchSize) {
      this.flushBatch();
    } else {
      // Schedule flush if not already scheduled
      this.scheduleFlush();
    }
  }

  /**
   * Schedule batch flush
   */
  private scheduleFlush(): void {
    if (this.flushTimer) {
      return; // Already scheduled
    }

    this.flushTimer = setTimeout(() => {
      this.flushBatch();
    }, this.config.flushInterval);
  }

  /**
   * Flush the current batch of events
   */
  private flushBatch(): void {
    if (this.eventBatch.length === 0) {
      return;
    }

    // Clear the flush timer
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    // Process all events in the batch
    const eventsToProcess = [...this.eventBatch];
    this.eventBatch = [];

    console.log(`AnalyticsMiddleware: Flushing batch of ${eventsToProcess.length} events`);

    for (const event of eventsToProcess) {
      try {
        analyticsService.logEvent(event.eventName, event.parameters);
      } catch (error) {
        console.error('AnalyticsMiddleware: Error logging batched event:', error);
      }
    }
  }

  /**
   * Create default event mappings for boat list actions
   * @return {Record<string, EventMapping>} Default event mappings
   */
  private createDefaultEventMappings(): Record<string, EventMapping> {
    return {
      'boatList/add': {
        eventName: 'boat_management',
        parameterExtractor: (action, state) => ({
          action_type: 'add_boat',
          feature_name: 'boat_list',
          boat_count: this.getBoatCount(state),
          group_id: state?.boatList.currentGroupId || 'unknown',
        }),
      },
      'boatList/remove': {
        eventName: 'boat_management',
        parameterExtractor: (action, state) => ({
          action_type: 'remove_boat',
          feature_name: 'boat_list',
          boat_count: this.getBoatCount(state),
          group_id: state?.boatList.currentGroupId || 'unknown',
        }),
      },
      'boatList/update': {
        eventName: 'boat_management',
        parameterExtractor: (action, state) => ({
          action_type: 'update_boat',
          feature_name: 'boat_list',
          boat_count: this.getBoatCount(state),
          group_id: state?.boatList.currentGroupId || 'unknown',
        }),
      },
      'boatList/selectBaseBoat': {
        eventName: 'handicap_system_selection',
        parameterExtractor: (action, state) => ({
          selected_system: this.extractHandicapSystem(action, state),
          selection_method: 'base_boat_selection',
          boat_count_affected: this.getBoatCount(state),
          group_id: state?.boatList.currentGroupId || 'unknown',
        }),
      },
      'boatList/replaceAll': {
        eventName: 'data_import',
        parameterExtractor: (action) => ({
          source_type: 'bulk_replace',
          record_count: action.payload?.boatGroupList?.length || 0,
          import_success: true,
        }),
      },
      'boatList/addGroup': {
        eventName: 'boat_management',
        parameterExtractor: (action, state) => ({
          action_type: 'add_group',
          feature_name: 'group_management',
          group_count: state?.boatList.boatGroupList.length || 0,
          handicap_system: action.payload?.handicapSystem || 'unknown',
        }),
      },
      'boatList/removeGroup': {
        eventName: 'boat_management',
        parameterExtractor: (action, state) => ({
          action_type: 'remove_group',
          feature_name: 'group_management',
          group_count: state?.boatList.boatGroupList.length || 0,
        }),
      },
      'boatList/updateGroup': {
        eventName: 'boat_management',
        parameterExtractor: (action, state) => ({
          action_type: 'update_group',
          feature_name: 'group_management',
          group_count: state?.boatList.boatGroupList.length || 0,
          handicap_system: action.payload?.handicapSystem || 'unknown',
        }),
      },
      'boatList/selectCurrentGroup': {
        eventName: 'handicap_system_selection',
        parameterExtractor: (action, state) => ({
          selected_system: this.extractGroupHandicapSystem(action, state),
          selection_method: 'group_selection',
          boat_count_affected: this.getBoatCountForGroup(action.payload, state),
          previous_group: this.getPreviousGroupId(state),
        }),
      },
    };
  }

  /**
   * Get total boat count from state
   * @param {RootState} state - Redux state
   * @return {number} Boat count
   */
  private getBoatCount(state?: RootState): number {
    if (!state?.boatList) return 0;

    const currentGroup = state.boatList.boatGroupList.find(
      (group) => group.id === state.boatList.currentGroupId,
    );

    return currentGroup?.boatDetails?.length || 0;
  }

  /**
   * Get boat count for a specific group
   * @param {string} groupId - Group ID
   * @param {RootState} state - Redux state
   * @return {number} Boat count for group
   */
  private getBoatCountForGroup(groupId: string, state?: RootState): number {
    if (!state?.boatList) return 0;

    const group = state.boatList.boatGroupList.find((g) => g.id === groupId);
    return group?.boatDetails?.length || 0;
  }

  /**
   * Extract handicap system from action/state
   * @param {AnyAction} action - Redux action
   * @param {RootState} state - Redux state
   * @return {string} Handicap system
   */
  private extractHandicapSystem(action: AnyAction, state?: RootState): string {
    // Try to get from current group
    if (state?.boatList) {
      const currentGroup = state.boatList.boatGroupList.find(
        (group) => group.id === state.boatList.currentGroupId,
      );
      if (currentGroup?.handicapSystem) {
        return currentGroup.handicapSystem;
      }
    }

    return 'unknown';
  }

  /**
   * Extract handicap system from group selection
   * @param {AnyAction} action - Redux action
   * @param {RootState} state - Redux state
   * @return {string} Handicap system
   */
  private extractGroupHandicapSystem(action: AnyAction, state?: RootState): string {
    if (!state?.boatList) return 'unknown';

    const group = state.boatList.boatGroupList.find((g) => g.id === action.payload);
    return group?.handicapSystem || 'unknown';
  }

  /**
   * Get previous group ID for tracking group changes
   * @param {RootState} state - Redux state
   * @return {string} Previous group ID
   */
  private getPreviousGroupId(state?: RootState): string {
    return state?.boatList.currentGroupId || 'unknown';
  }

  /**
   * Force flush any pending events (useful for cleanup)
   */
  public forceFlush(): void {
    this.flushBatch();
  }

  /**
   * Update middleware configuration
   * @param {Partial<AnalyticsMiddlewareConfig>} newConfig - New configuration
   */
  public updateConfig(newConfig: Partial<AnalyticsMiddlewareConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Export singleton instance
export const analyticsMiddleware = new AnalyticsMiddleware();
export default analyticsMiddleware;
